import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, CheckCircle, FileText, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function InsurancePage() {
  const coverageItems = [
    "Comprehensive damage coverage up to $150,000",
    "Coverage for weather-related damage",
    "Protection against road debris damage",
    "Coverage for loading/unloading incidents",
    "Theft protection during transit",
    "Total loss coverage"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="overline mb-4">Protection</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="insurance-title">
              Insurance Coverage
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Your Tesla is protected every mile of the journey with comprehensive insurance coverage.
            </p>
          </div>

          {/* Main Coverage Card */}
          <Card className="glass border-accent/50 mb-8">
            <CardHeader className="text-center pb-8">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-accent" />
              </div>
              <CardTitle className="font-outfit text-3xl">Up to $150,000 Coverage</CardTitle>
              <p className="text-muted-foreground mt-2">
                Every transport is backed by comprehensive cargo insurance
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {coverageItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card className="glass border-border">
              <CardHeader>
                <FileText className="h-6 w-6 text-accent mb-2" />
                <CardTitle className="font-outfit text-lg">Inspection Process</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p className="mb-4">
                  Before and after transport, we conduct thorough vehicle inspections:
                </p>
                <ul className="space-y-2">
                  <li>"¢ Detailed condition report at pickup</li>
                  <li>"¢ Photo documentation of all sides</li>
                  <li>"¢ Notation of existing damage</li>
                  <li>"¢ Final inspection at delivery</li>
                  <li>"¢ Signed Bill of Lading</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="glass border-border">
              <CardHeader>
                <AlertTriangle className="h-6 w-6 text-accent mb-2" />
                <CardTitle className="font-outfit text-lg">Claims Process</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p className="mb-4">
                  In the unlikely event of damage during transport:
                </p>
                <ul className="space-y-2">
                  <li>"¢ Note damage on delivery receipt</li>
                  <li>"¢ Take photos of any issues</li>
                  <li>"¢ Contact us within 24 hours</li>
                  <li>"¢ We handle the claim process</li>
                  <li>"¢ Fast resolution and payout</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <div className="glass border border-border rounded-2xl p-8 mb-12">
            <h2 className="font-outfit text-xl font-semibold mb-6">Insurance Requirements</h2>
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>
                All carriers in our network are required to maintain:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <li>"¢ Active USDOT registration</li>
                <li>"¢ Valid MC authority number</li>
                <li>"¢ Minimum $750,000 liability insurance</li>
                <li>"¢ Cargo insurance of at least $100,000</li>
                <li>"¢ Workers' compensation coverage</li>
                <li>"¢ Current safety ratings</li>
              </ul>
              <p className="mt-4">
                We verify all carrier credentials before adding them to our network and conduct regular audits to ensure ongoing compliance.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-muted-foreground mb-6">
              Ready to ship your Tesla with peace of mind?
            </p>
            <Link to="/quote">
              <Button className="bg-accent text-accent-foreground rounded-full px-8 py-6 dark:hover:text-black">
                Get Your Insured Quote
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

